import React, { useEffect, useState } from 'react';
import { ShoppingBag } from 'lucide-react';

/**
 * FloatingCartFab — big, always-visible floating cart button anchored to
 * the bottom-right of every page. Reads cart count from the Shopify SDK's
 * toggle iframe (which the SDK creates after the first BUY click) and
 * clicks through to open the drawer.
 *
 * Built so customers ALWAYS see a clear path to checkout regardless of
 * scroll position, viewport size, or navbar layout. Pairs with the
 * navbar's ShopifyCartToggle but exists independently — visibility is
 * non-negotiable on the purchase path.
 */

const findToggleIframe = () => {
  const frames = Array.from(document.querySelectorAll('iframe'));
  for (const f of frames) {
    try {
      const doc = f.contentDocument;
      if (doc && doc.querySelector('.shopify-buy__cart-toggle')) return f;
    } catch (_) { /* cross-origin */ }
  }
  return null;
};

const readCount = () => {
  const iframe = findToggleIframe();
  if (!iframe || !iframe.contentDocument) return 0;
  const el = iframe.contentDocument.querySelector('.shopify-buy__cart-toggle__count');
  if (!el) return 0;
  const n = parseInt((el.textContent || '0').trim(), 10);
  return Number.isFinite(n) ? n : 0;
};

const openCart = () => {
  const iframe = findToggleIframe();
  if (!iframe || !iframe.contentDocument) return;
  const wasHidden = iframe.style.display === 'none';
  if (wasHidden) { iframe.style.display = ''; iframe.style.visibility = ''; }
  const btn =
    iframe.contentDocument.querySelector('.shopify-buy__cart-toggle') ||
    iframe.contentDocument.querySelector('button');
  if (btn) btn.click();
  if (wasHidden) setTimeout(() => { iframe.style.display = 'none'; iframe.style.visibility = 'hidden'; }, 100);
};

const FloatingCartFab = () => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const tick = () => setCount(readCount());
    tick();
    const id = setInterval(tick, 800);
    return () => clearInterval(id);
  }, []);

  return (
    <button
      onClick={openCart}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-wider rounded-full shadow-2xl hover:bg-[#E5C048] active:scale-95 transition-all"
      data-testid="floating-cart-fab"
      aria-label={`Cart (${count} items)`}
      style={{ boxShadow: '0 12px 32px -6px rgba(212, 175, 55, 0.5), 0 4px 16px rgba(0,0,0,0.4)' }}
    >
      <ShoppingBag size={20} strokeWidth={2.25} />
      <span className="text-sm">{count > 0 ? `Cart (${count})` : 'Cart'}</span>
    </button>
  );
};

export default FloatingCartFab;
